import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { useTranslation } from 'react-i18next';

import Button from 'components/field/Button';
import SearchInput from 'components/field/SearchInput';
import Popper from 'components/global/Popper';

import useUpdateEffect from 'hooks/useUpdateEffect';

import * as S from '../Table.styled';
import TableColumnsDndItem from './TableColumnsDndItem';

const TableColumnsMenu = (props) => {
	const { table, columns, storedColumns, setStoredColumns, columnsMenuButtonRef } = props;

	const { t } = useTranslation();

	// Helper function to check if a column is visibility changeable
	const isVisibilityChangeableColumn = useCallback(
		(col) =>
			col.id !== 'select' && col.id !== 'editRow' && col.id !== 'deleteRow' && col.showInColumnsMenu !== false,
		[]
	);

	const getVisibilityChangeableColumns = useCallback(
		() => columns.filter(isVisibilityChangeableColumn),
		[columns, isVisibilityChangeableColumn]
	);

	const isSingleVisibleColumn = useMemo(() => columns.filter((col) => col.show !== false).length <= 1, [columns]);

	const [columnsMenuSearchValue, setColumnsMenuSearchValue] = useState(null);
	const [filteredColumnsMenuColumns, setFilteredColumnsMenuColumns] = useState(getVisibilityChangeableColumns());

	useUpdateEffect(() => {
		const visibilityChangeableColumns = getVisibilityChangeableColumns();

		if (columnsMenuSearchValue) {
			setFilteredColumnsMenuColumns(
				visibilityChangeableColumns.filter((col) =>
					t(col.header).toLowerCase().includes(columnsMenuSearchValue.toLowerCase())
				)
			);
		} else {
			setFilteredColumnsMenuColumns(visibilityChangeableColumns);
		}
	}, [columnsMenuSearchValue, columns, getVisibilityChangeableColumns, t]);

	const handleColumnVisibilityChange = useCallback(
		(column) => {
			const columnsTemplate = structuredClone(
				getVisibilityChangeableColumns().map((col) => ({
					id: col.id,
					accessorKey: col.accessorKey,
					size: col.size,
					enableHiding: col.enableHiding,
					show: col.show
				}))
			);

			columnsTemplate.find((col) =>
				column.id && col.id ? col.id === column.id : col.accessorKey === column.accessorKey
			).show = column.show === false;

			setStoredColumns(columnsTemplate);
		},
		[columns, setStoredColumns, getVisibilityChangeableColumns]
	);

	const handleColumnOrderChange = useCallback(
		(dragIndex, hoverIndex) => {
			setStoredColumns((prevState) => {
				if (prevState.length > 0) {
					const newState = structuredClone(prevState);
					newState.splice(dragIndex, 1);
					newState.splice(hoverIndex, 0, prevState[dragIndex]);
					return newState;
				} else {
					const preparedColumns = getVisibilityChangeableColumns().map((col) => ({
						id: col.id,
						accessorKey: col.accessorKey,
						size: col.size,
						enableHiding: col.enableHiding,
						show: col.show
					}));
					const newState = structuredClone(preparedColumns);
					newState.splice(dragIndex, 1);
					newState.splice(hoverIndex, 0, preparedColumns[dragIndex]);
					return newState;
				}
			});
		},
		[columns, setStoredColumns, getVisibilityChangeableColumns]
	);

	const isUnmounting = useRef(false);

	useEffect(
		() => () => {
			isUnmounting.current = true;
		},
		[]
	);

	// save column sizes on unmount
	useEffect(
		() => () => {
			if (isUnmounting.current) {
				const allColumns = table.getAllColumns();

				const columnsTemplate =
					storedColumns.length > 0
						? storedColumns
						: structuredClone(
								getVisibilityChangeableColumns().map((col) => ({
									id: col.id,
									accessorKey: col.accessorKey,
									size: col.size,
									enableHiding: col.enableHiding,
									show: col.show
								}))
							);

				const isAnySizeChanged = allColumns.some((column) => {
					const foundColumn = columnsTemplate.find((col) =>
						column.id && col.id ? col.id === column.id : col.accessorKey === column.columnDef.accessorKey
					);

					if (!foundColumn) return false;

					return foundColumn.size !== column.getSize();
				});

				if (isAnySizeChanged) {
					const newColumns = columnsTemplate.map((column) => {
						const foundColumn = allColumns.find((col) =>
							column.id && col.id
								? col.id === column.id
								: column.accessorKey === col.columnDef.accessorKey
						);

						return {
							...column,
							size: foundColumn.getSize()
						};
					});

					setStoredColumns(newColumns);
				}
			}
		},
		[table, storedColumns, setStoredColumns, getVisibilityChangeableColumns]
	);

	return (
		columnsMenuButtonRef && (
			<Popper refElement={columnsMenuButtonRef} placement="bottom-end">
				{() => (
					<S.ColumnsMenu>
						<SearchInput value={columnsMenuSearchValue} setValue={setColumnsMenuSearchValue} fullWidth />
						<DndProvider backend={HTML5Backend}>
							{filteredColumnsMenuColumns.map((column, index) => (
								<TableColumnsDndItem
									key={column.id ?? column.accessorKey}
									index={index}
									column={column}
									onColumnOrderChange={handleColumnOrderChange}
									onColumnVisibilityChange={handleColumnVisibilityChange}
									isSingleVisibleColumn={isSingleVisibleColumn}
								/>
							))}
						</DndProvider>
						<Button
							id="table-reset-columns-button"
							className="table-reset-columns-button"
							label="resetColumns"
							variant="transparent"
							color="red"
							onClick={() => {
								setStoredColumns([]);
								table.resetColumnSizing();
							}}
							fullWidth
						/>
					</S.ColumnsMenu>
				)}
			</Popper>
		)
	);
};

export default TableColumnsMenu;
